import { useState, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { getWinLossSummary, type WinLossSummary } from '../lib/api'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

function SignalBadge({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="bg-carbon text-steel text-footnote px-2 py-0.5 rounded-full">—</span>
  if (value === 'win') return <span className="badge-success text-footnote px-2 py-0.5 rounded-full">Win</span>
  if (value === 'loss') return <span style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }} className="text-footnote px-2 py-0.5 rounded-full border border-red-500/30">Loss</span>
  if (value === 'nurture') return <span style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }} className="text-footnote px-2 py-0.5 rounded-full border border-yellow-500/30">Nurture</span>
  if (value === 'pending') return <span className="bg-carbon text-steel text-footnote px-2 py-0.5 rounded-full">Pending</span>
  return <span className="bg-carbon text-steel text-footnote px-2 py-0.5 rounded-full capitalize">{value}</span>
}

type Row = WinLossSummary['recent'][number]

export default function AdminWinLoss() {
  const { user, isLoaded } = useUser()
  const [data, setData] = useState<WinLossSummary | null>(null)
  const [loading, setLoading] = useState(true)

  const email = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? ''
  const isAdmin = isLoaded && !!user && ADMIN_EMAILS.includes(email)

  useEffect(() => {
    if (!isAdmin) return
    setLoading(true)
    getWinLossSummary()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [isAdmin])

  if (!isLoaded) return <div className="min-h-screen bg-void" />

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <p className="text-body text-steel">Access denied</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-signal" />
      </div>
    )
  }

  const rows: Row[] = data?.recent ?? []
  const counts = data?.counts ?? { win: 0, loss: 0, nurture: 0, pending: 0, unset: 0 }

  return (
    <div className="p-8 space-y-8 max-w-5xl">
      {/* Header */}
      <div>
        <h1 className="text-h1 text-chalk">Win / Loss</h1>
        <p className="text-body-sm text-steel mt-1">Discovery call signal tracker</p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 tablet:grid-cols-5 gap-4">
        <div className="bg-obsidian border border-carbon rounded p-4 text-center">
          <div className="text-h2 font-bold text-chalk">{data?.total ?? 0}</div>
          <div className="text-footnote text-steel mt-1">Discovery Calls</div>
        </div>
        <div className="bg-obsidian border border-green-500/20 rounded p-4 text-center">
          <div className="text-h2 font-bold text-green-400">{counts.win}</div>
          <div className="text-footnote text-steel mt-1">Win</div>
        </div>
        <div className="bg-obsidian border border-red-500/20 rounded p-4 text-center">
          <div className="text-h2 font-bold text-red-400">{counts.loss}</div>
          <div className="text-footnote text-steel mt-1">Loss</div>
        </div>
        <div className="bg-obsidian border border-yellow-500/20 rounded p-4 text-center">
          <div className="text-h2 font-bold text-yellow-400">{counts.nurture}</div>
          <div className="text-footnote text-steel mt-1">Nurture</div>
        </div>
        <div className="bg-obsidian border border-carbon rounded p-4 text-center">
          <div className="text-h2 font-bold text-steel">{counts.pending}</div>
          <div className="text-footnote text-steel mt-1">Pending</div>
        </div>
      </div>

      {/* Table */}
      {rows.length === 0 ? (
        <div className="bg-obsidian border border-carbon rounded p-8 text-center">
          <p className="text-steel text-body">No discovery calls logged yet.</p>
        </div>
      ) : (
        <div className="bg-obsidian border border-carbon rounded overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead>
                <tr className="border-b border-carbon">
                  <th className="text-left px-4 py-3 text-footnote text-steel font-medium">Company</th>
                  <th className="text-left px-4 py-3 text-footnote text-steel font-medium">Contact</th>
                  <th className="text-left px-4 py-3 text-footnote text-steel font-medium">Signal</th>
                  <th className="text-left px-4 py-3 text-footnote text-steel font-medium">Current Stack</th>
                  <th className="text-left px-4 py-3 text-footnote text-steel font-medium">Pain Points</th>
                  <th className="text-left px-4 py-3 text-footnote text-steel font-medium">Demo</th>
                  <th className="text-left px-4 py-3 text-footnote text-steel font-medium">Design Partner</th>
                  <th className="text-left px-4 py-3 text-footnote text-steel font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.id} className={`border-b border-carbon/50 ${i % 2 === 0 ? '' : 'bg-carbon/10'}`}>
                    <td className="px-4 py-3 text-chalk font-medium whitespace-nowrap">
                      {row.company ?? <span className="text-steel">—</span>}
                    </td>
                    <td className="px-4 py-3 text-steel whitespace-nowrap">
                      {row.full_name ?? <span className="text-steel/50">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <SignalBadge value={row.metadata?.win_loss as string | null | undefined} />
                    </td>
                    <td className="px-4 py-3 text-steel max-w-[160px]">
                      <span className="line-clamp-2">{(row.metadata?.current_stack as string) || <span className="text-steel/40">—</span>}</span>
                    </td>
                    <td className="px-4 py-3 text-steel max-w-[200px]">
                      <span className="line-clamp-2">{(row.metadata?.pain_points as string) || <span className="text-steel/40">—</span>}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {row.metadata?.demo_requested
                        ? <span className="text-signal text-footnote">✓ Yes</span>
                        : <span className="text-steel/40 text-footnote">No</span>
                      }
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {row.metadata?.design_partner_interest
                        ? <span className="text-steel text-footnote capitalize">{String(row.metadata.design_partner_interest)}</span>
                        : <span className="text-steel/40 text-footnote">—</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-steel/60 whitespace-nowrap text-footnote">
                      {new Date(row.occurred_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
